import { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";

const SNOOZE_MS = 7 * 24 * 3600 * 1000;

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const installed = window.matchMedia("(display-mode: standalone)").matches || navigator.standalone;
    if (installed) return;
    const dismissed = Number(localStorage.getItem("ezship_install_dismissed") || 0);
    if (dismissed && Date.now() - dismissed < SNOOZE_MS) return;
    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    const timer = setTimeout(() => setShow(true), 1800);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      clearTimeout(timer);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") {
      setShow(false);
      localStorage.setItem("ezship_install_dismissed", String(Date.now()));
    }
    setDeferred(null);
  };

  const dismiss = () => {
    setShow(false);
    localStorage.setItem("ezship_install_dismissed", String(Date.now()));
  };

  if (!show) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 p-4 flex justify-center" data-testid="install-prompt">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-xl shadow-xl p-5 relative">
        <button
          onClick={dismiss}
          data-testid="install-dismiss"
          className="absolute top-3 right-3 p-1.5 rounded-sm text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors duration-150"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-start gap-4">
          <img src="/pwa-icon-192.png" alt="EZShip" className="h-14 w-14 rounded-lg border border-slate-200 shrink-0" />
          <div className="min-w-0">
            <div className="text-base font-bold text-slate-900">Install EZShip Corporate</div>
            {isIOS ? (
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                Tap the <Share className="inline h-4 w-4 -mt-0.5 text-primary" /> <span className="font-semibold">Share</span> button in Safari, then choose <span className="font-semibold">"Add to Home Screen"</span> to install the app.
              </p>
            ) : deferred ? (
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                Add EZShip to your home screen for one-tap access — works like a native app.
              </p>
            ) : (
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                Open your browser menu (<span className="font-semibold">⋮</span>) and tap <span className="font-semibold">"Install app"</span> or <span className="font-semibold">"Add to Home screen"</span> to install EZShip.
              </p>
            )}
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          {!isIOS && deferred && (
            <button
              onClick={install}
              data-testid="install-accept"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-primary hover:bg-[#152D53] text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors duration-150"
            >
              <Download className="h-4 w-4" /> Install App
            </button>
          )}
          <button
            onClick={dismiss}
            data-testid="install-not-now"
            className={`${!isIOS && deferred ? "" : "flex-1"} px-4 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors duration-150`}
          >
            {isIOS || !deferred ? "Got it" : "Not now"}
          </button>
        </div>
      </div>
    </div>
  );
}
